import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../../../i18n/i18n.constants";
import { useEffect } from "react";
import { FormikProps, useFormik } from "formik";
import * as yup from "yup";
import CompanyInputFormElements from "../../../../features/auth/signUp/forms/CompanyInputFormElements";
import { CompanyResponse } from "../../../../app/models/api/company/companyResponse";
import { CompanyData, UserCompanyData } from "../../../../app/models/api/auth/signUpRequest";
import { UpdateCompanyRequest } from "../../../../app/models/api/company/updateCompanyRequest";
import {
    useCreateCompanyByUserMutation,
    useUpdateCompanyByUserMutation,
} from "../../../../app/api/companyApi";
import { DeliveryEstimateType } from "../../../../app/enums/deliveryEstimateEnum";
import CustomSpinner from "../../../../components/spinner/CustomSpinner";
import { mapLangToCountryCode } from "../../../../services/format/countryAndLangCodes";
import "./CompanyModal.scss";

type Props = {
    currentCompany?: CompanyResponse;
    onClose(): void;
    isShow: boolean;
    readOnly?: boolean;
    onDelete?(): void;
};

const CompanyModal = ({ currentCompany, onClose, isShow, readOnly, onDelete }: Props) => {
    const { t, i18n } = useTranslation(namespaces.modals);

    const [createCompanyHandler, { isLoading: isCreateLoading, isSuccess: isCreateSuccess }] =
        useCreateCompanyByUserMutation();

    const [updateCompanyHandler, { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess }] =
        useUpdateCompanyByUserMutation();

    useEffect(() => {
        if (isCreateSuccess || isUpdateSuccess) {
            onClose();
        }
    }, [isCreateSuccess, isUpdateSuccess, onClose]);

    const isEditMode = !!currentCompany;

    const formik = useFormik<CompanyData>({
        enableReinitialize: true,
        validationSchema: yup.object().shape({
            companyName: yup.string().required(t("required.field", { ns: namespaces.validation })),
            countryCode: yup.string().required(t("required.field", { ns: namespaces.validation })),
            address: yup.string().required(t("required.field", { ns: namespaces.validation })),
            zip: yup.string().required(t("required.field", { ns: namespaces.validation })),
            city: yup.string().required(t("required.field", { ns: namespaces.validation })),
        }),
        initialValues: {
            countryCode: currentCompany?.countryCode ?? mapLangToCountryCode(i18n.language).toLocaleUpperCase(),
            companyName: currentCompany?.companyName ?? "",
            address: currentCompany?.address ?? "",
            zip: currentCompany?.zip ?? "",
            city: currentCompany?.city ?? "",
            tin: currentCompany?.tin ?? "",
        },
        onSubmit: async (values, { setSubmitting }) => {
            if (isEditMode) {
                const updateRequest: UpdateCompanyRequest = {
                    id: currentCompany!.id,
                    companyName: values.companyName,
                    countryCode: values.countryCode,
                    zip: values.zip,
                    city: values.city,
                    address: values.address,
                    tin: values.tin,
                };
                await updateCompanyHandler(updateRequest);
            } else {
                const createRequest: UserCompanyData = {
                    companyName: values.companyName,
                    countryCode: values.countryCode,
                    zip: values.zip,
                    city: values.city,
                    address: values.address,
                    tin: values.tin,
                    deliveryEstimateType: DeliveryEstimateType.LessThan5,
                    consent: true,
                };
                await createCompanyHandler(createRequest);
            }
            setSubmitting(true);
        },
    });

    return (
        <Modal
            show={isShow}
            size="lg"
            onHide={onClose}
            backdrop="static"
            backdropClassName="blur-backdrop"
            centered
            fullscreen="sm-down"
            className="company-modal"
            contentClassName="company-modal-content"
            aria-labelledby="company-modal"
        >
            <Modal.Header closeButton className="px-5">
                <div className="d-flex align-items-center flex-grow-1">
                    <span className="fs-4 fw-bold">
                        {isEditMode
                            ? t("company.editTitle")
                            : t("company.addTitle")}
                    </span>
                </div>
            </Modal.Header>
            <Modal.Body className="py-4 px-5">
                {isCreateLoading || isUpdateLoading ? (
                    <CustomSpinner hideTextTitle={true} />
                ) : (
                    <Form onSubmit={formik.handleSubmit}>
                        <CompanyInputFormElements formik={formik as FormikProps<CompanyData>} />
                        {!readOnly && (
                            <div className="d-flex flex-column gap-2 my-3">
                                <Button
                                    variant="primary"
                                    disabled={formik.isSubmitting || !formik.isValid || (isEditMode && !formik.dirty)}
                                    type="submit"
                                    className="w-100 p-3"
                                >
                                    {t("buttons.save", { ns: namespaces.common })}
                                </Button>
                                {isEditMode && onDelete && (
                                    <Button
                                        variant="outline-danger"
                                        className="w-100 p-3"
                                        onClick={onDelete}
                                    >
                                        {t("buttons.delete", { ns: namespaces.common })}
                                    </Button>
                                )}
                            </div>
                        )}
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default CompanyModal;
